import { useMemo } from 'react';
import { useSelector } from 'react-redux';

/**
 * Widget visibility configuration by role
 * Note: SUPER_ADMIN viewing as org gets ORG_ADMIN-level widget access
 */
const WIDGET_ACCESS = {
  KPI_SUMMARY: ['ADMIN', 'ADMIN', 'MANAGER', 'MANAGER', 'SUPER_ADMIN'],
  TEAM_STATUS_GRID: ['ADMIN', 'ADMIN', 'MANAGER', 'MANAGER', 'SUPER_ADMIN'],
  LOCATION_HEATMAP: ['ADMIN', 'ADMIN', 'SUPER_ADMIN'],
  ATTENDANCE_TRENDS_ORG: ['ADMIN', 'ADMIN', 'SUPER_ADMIN'],
  ATTENDANCE_TRENDS_TEAM: ['ADMIN', 'ADMIN', 'MANAGER', 'MANAGER', 'SUPER_ADMIN'],
  MY_ATTENDANCE_CALENDAR: ['ADMIN', 'ADMIN', 'MANAGER', 'MANAGER', 'MEMBER', 'SUPER_ADMIN'],
  PENDING_APPROVALS: ['ADMIN', 'ADMIN', 'MANAGER', 'MANAGER', 'SUPER_ADMIN'],
  GEOFENCE_VIOLATIONS: ['ADMIN', 'ADMIN', 'MANAGER', 'MANAGER', 'SUPER_ADMIN'],
  HOURS_DISTRIBUTION: ['ADMIN', 'ADMIN', 'MANAGER', 'MANAGER', 'SUPER_ADMIN'],
  DIGITAL_WELLBEING: ['ADMIN', 'ADMIN', 'MANAGER', 'MANAGER', 'MEMBER', 'SUPER_ADMIN'],
  LOCATION_TRAIL: ['ADMIN', 'ADMIN', 'MANAGER', 'MANAGER', 'MEMBER', 'SUPER_ADMIN'],
  PERSONAL_SUMMARY: ['ADMIN', 'ADMIN', 'MANAGER', 'MANAGER', 'MEMBER', 'SUPER_ADMIN'],
};

/**
 * Custom hook for role-based widget access
 * Supports Super Admin viewing data as specific organization
 */
export const useRoleAccess = () => {
  const loginResponse = useSelector((state) => state.loginResponse);
  // Get Super Admin selected organization from Redux
  const superAdminSelectedOrgId = useSelector((state) => state.superAdminSelectedOrgId);
  const superAdminSelectedOrgData = useSelector((state) => state.superAdminSelectedOrgData);
  const superAdminSelectedSubOrgId = superAdminSelectedOrgData?.type === 'sub_org' ? superAdminSelectedOrgData?.sub_org_id : null;

  const userRole = useMemo(() => {
    const role = loginResponse?.role_name || loginResponse?.roleName || 'MEMBER';
    return role.toUpperCase();
  }, [loginResponse]);

  // Check if current user is Super Admin
  const isSuperAdmin = useMemo(() => {
    return userRole === 'SUPER_ADMIN';
  }, [userRole]);

  const userId = useMemo(() => {
    return loginResponse?.user_id || loginResponse?.userId || '';
  }, [loginResponse]);

  // For Super Admin: use selected org_id if set, otherwise use logged-in org_id
  const orgId = useMemo(() => {
    if (isSuperAdmin && superAdminSelectedOrgId) {
      return superAdminSelectedOrgId;
    }
    return loginResponse?.org_id || loginResponse?.orgId || '';
  }, [loginResponse, isSuperAdmin, superAdminSelectedOrgId]);

  const userName = useMemo(() => {
    return loginResponse?.full_name || loginResponse?.fullName || '';
  }, [loginResponse]);

  /**
   * Check if user can view a specific widget
   */
  const canViewWidget = (widgetName) => {
    const allowedRoles = WIDGET_ACCESS[widgetName];
    if (!allowedRoles) return false;
    return allowedRoles.includes(userRole);
  };

  /**
   * Check if user is admin (or Super Admin viewing as org - gets admin access)
   */
  const isAdmin = useMemo(() => {
    // Super Admin viewing as org gets admin-level access
    if (isSuperAdmin && superAdminSelectedOrgId) {
      return true;
    }
    return userRole === 'ADMIN' || userRole === 'ADMIN';
  }, [userRole, isSuperAdmin, superAdminSelectedOrgId]);

  /**
   * Check if user is manager
   */
  const isManager = useMemo(() => {
    return userRole === 'MANAGER' || userRole === 'MANAGER';
  }, [userRole]);

  /**
   * Check if user is member/employee
   */
  const isMember = useMemo(() => {
    return userRole === 'MEMBER' || userRole === 'MEMBER';
  }, [userRole]);

  /**
   * Get dashboard layout based on role
   */
  const getDashboardLayout = () => {
    // Super Admin viewing as org gets admin layout
    if (isSuperAdmin && superAdminSelectedOrgId) {
      return 'admin';
    }
    if (isAdmin) {
      return 'admin'; // Full dashboard with all widgets
    }
    if (isManager) {
      return 'manager'; // Team-focused dashboard
    }
    return 'member'; // Personal dashboard
  };

  /**
   * Get visible widgets for current role
   */
  const getVisibleWidgets = () => {
    return Object.entries(WIDGET_ACCESS)
      .filter(([_, roles]) => roles.includes(userRole))
      .map(([widget]) => widget);
  };

  return {
    userRole,
    userId,
    orgId,
    userName,
    isAdmin,
    isManager,
    isMember,
    isSuperAdmin,
    superAdminSelectedOrgId,
    superAdminSelectedSubOrgId,
    canViewWidget,
    getDashboardLayout,
    getVisibleWidgets,
  };
};

export default useRoleAccess;
